import React, { useState, useEffect } from 'react'

const OccasionPreview = ({ template_id, occasionConfig }) => {
  const [nextDates, setNextDates] = useState([])
  const [currentCheck, setCurrentCheck] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Fetch next applicable dates
  const fetchNextDates = async () => {
    if (!template_id || !occasionConfig.isOccasionDateApplicable) {
      setNextDates([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `/api/v1/templates/${template_id}/occasion/next-dates?limit=10`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setNextDates(data.data.nextDates || [])
      } else {
        throw new Error('Failed to fetch next dates')
      }
    } catch (err) {
      setError(err.message)
      setNextDates([])
    } finally {
      setLoading(false)
    }
  }

  // Check current applicability
  const checkCurrentApplicability = async () => {
    if (!template_id || !occasionConfig.isOccasionDateApplicable) {
      setCurrentCheck(null)
      return
    }

    try {
      const now = new Date().toISOString()
      const response = await fetch(
        `/api/v1/templates/${template_id}/occasion/check?check_date=${now}&timezone=${occasionConfig.occasionTimezone || 'UTC'}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setCurrentCheck(data.data)
      }
    } catch (err) {
      console.error('Failed to check current applicability:', err)
    }
  }

  // Update when config changes
  useEffect(() => {
    fetchNextDates()
    checkCurrentApplicability()
  }, [template_id, occasionConfig])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString('en-IN', {
        timeZone: 'Asia/Kolkata',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }),
      relative: getRelativeTime(date)
    }
  }

  const getRelativeTime = (date) => {
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays === -1) return 'Yesterday'
    if (diffDays > 0) return `In ${diffDays} days`
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`
    return 'Unknown'
  }

  const getOccasionTypeDescription = () => {
    switch (occasionConfig.occasionType) {
      case 'specific_date':
        return 'Available on a specific date'
      case 'date_range':
        return 'Available during a date range'
      case 'recurring_yearly':
        return 'Same date range repeats every year'
      case 'weekly':
        return 'Available on specific days of the week'
      case 'monthly':
        return 'Available on specific days/patterns each month'
      default:
        return 'No occasion restrictions'
    }
  }

  if (!occasionConfig.isOccasionDateApplicable) {
    return (
      <div className="p-6 text-center text-gray-500">
        <div className="text-4xl mb-2">🔄</div>
        <p>Occasion scheduling is disabled. Template is always available.</p>
      </div>
    )
  }

  return (
    <div className="occasion-preview p-6 bg-gray-50">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-semibold text-gray-900">
          Schedule Preview
        </h4>
        <button
          onClick={() => {
            fetchNextDates()
            checkCurrentApplicability()
          }}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Current Status */}
      <div className="mb-6">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Current Status</h5>
        {currentCheck ? (
          <div className={`p-3 rounded-lg border ${
            currentCheck.isApplicable
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center">
              <div className={`mr-2 ${
                currentCheck.isApplicable ? 'text-green-500' : 'text-red-500'
              }`}>
                {currentCheck.isApplicable ? '✅' : '❌'}
              </div>
              <div>
                <div className={`text-sm font-medium ${
                  currentCheck.isApplicable ? 'text-green-800' : 'text-red-800'
                }`}>
                  {currentCheck.isApplicable ? 'Currently Available' : 'Currently Not Available'}
                </div>
                <div className={`text-xs ${
                  currentCheck.isApplicable ? 'text-green-600' : 'text-red-600'
                }`}>
                  {getOccasionTypeDescription()}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-gray-100 rounded-lg">
            <div className="text-sm text-gray-600">
              {loading ? 'Checking current status...' : 'Status unknown'}
            </div>
          </div>
        )}
      </div>

      {/* Next Applicable Dates */}
      <div>
        <h5 className="text-sm font-medium text-gray-700 mb-3">
          Next Applicable Dates
        </h5>
        
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="text-red-500 mr-2">⚠️</div>
              <div>
                <div className="text-sm font-medium text-red-800">Error Loading Dates</div>
                <div className="text-xs text-red-600">{error}</div>
              </div>
            </div>
          </div>
        ) : nextDates.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {nextDates.map((dateString, index) => {
              const formatted = formatDate(dateString)
              
              return (
                <div
                  key={index}
                  className="p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {formatted.date}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatted.time} • {formatted.relative}
                      </div>
                    </div>
                    
                    <div className="text-xs text-blue-600 font-medium">
                      #{index + 1}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <div className="text-yellow-500 mr-2">⚠️</div>
              <div>
                <div className="text-sm font-medium text-yellow-800">
                  No Upcoming Dates
                </div>
                <div className="text-xs text-yellow-600">
                  No applicable dates found in the next 2 years. Check your configuration.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Configuration Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Configuration</h5>
        <div className="text-xs text-gray-600 space-y-1">
          <div>Type: <span className="font-medium">{occasionConfig.occasionType}</span></div>
          <div>Timezone: <span className="font-medium">{occasionConfig.occasionTimezone}</span></div>
          
          {occasionConfig.occasionStartDate && (
            <div>Start: <span className="font-medium">
              {new Date(occasionConfig.occasionStartDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}
            </span></div>
          )}
          
          {occasionConfig.occasionEndDate && (
            <div>End: <span className="font-medium">
              {new Date(occasionConfig.occasionEndDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}
            </span></div>
          )}
          
          {occasionConfig.weeklyRecurrenceDays && occasionConfig.weeklyRecurrenceDays.length > 0 && (
            <div>Weekly Days: <span className="font-medium">
              {occasionConfig.weeklyRecurrenceDays.join(', ')}
            </span></div>
          )}
          
          {occasionConfig.monthlyRecurrenceDays && occasionConfig.monthlyRecurrenceDays.length > 0 && (
            <div>Monthly Pattern: <span className="font-medium">
              {occasionConfig.monthlyRecurrenceDays.join(', ')}
            </span></div>
          )}
        </div>
      </div>
    </div>
  )
}

export { OccasionPreview }
